/**
 * Web Audio engine — buses, loader, SFX, StateSonifier, ducking, MusicDirector stub.
 */

import type { AudioBusGraph } from "./buses";
import { createBusGraph } from "./buses";
import {
  CLIP_COOLDOWN_S,
  clipAllowedInMinimal,
  getCatalogEntry,
  PRELOAD_MUSIC_IDS,
  PRELOAD_STATE_IDS,
  resolveEventClips,
  type ClipId,
} from "./catalog";
import {
  allowDevSynthFallback,
  forceAudioSynth,
} from "./devMode";
import { DUCK_ATTACK_S, DuckingController } from "./ducking";
import { renderLoopBedBuffer, renderSynthBuffer } from "./devSynth";
import type { AudioEvent, ProclaimKaraokeDriver } from "./events";
import { linearKaraokeDriver } from "./events";
import type { AudioFrameInput } from "./frame";
import { AudioLoader } from "./loader";
import { MusicDirector } from "./MusicDirector";
import { createNullEngine } from "./nullEngine";
import {
  audioDisabledByQuery,
  DEFAULT_AUDIO_SETTINGS,
  loadAudioSettings,
  mergeAudioSettings,
  saveAudioSettings,
  type AudioBusId,
  type AudioSettings,
} from "./settings";
import { SfxPlayer } from "./SfxPlayer";
import { StateSonifier } from "./StateSonifier";
import type { AudioEngine } from "./types";
import { LieVoicePlayer } from "./LieVoicePlayer";
import { PROCLAIM_VO_IDS, VoicePlayer } from "./VoicePlayer";

export type CreateAudioEngineOptions = {
  /**
   * When false, returns null engine (no AudioContext).
   * Default true in browser; forced null when AudioContext unavailable.
   */
  enabled?: boolean;
  /** Skip reading localStorage (tests). */
  settings?: AudioSettings;
  /** Override location.search for `?audio=0` / `?audioSynth=1` (tests). */
  search?: string;
  /** Persist settings on apply (default true in browser). */
  persist?: boolean;
  /**
   * Enable music/ambient loops when assets are present.
   * Default false (music layer incomplete). Set true to enable beds.
   */
  musicContent?: boolean;
};

class WebAudioEngine implements AudioEngine {
  readonly kind = "web" as const;

  private readonly context: AudioContext;
  private readonly graph: AudioBusGraph;
  private readonly loader: AudioLoader;
  private readonly sfx: SfxPlayer;
  private readonly state: StateSonifier;
  private readonly music: MusicDirector;
  private readonly voice: VoicePlayer;
  private readonly lieVoice: LieVoicePlayer;
  private readonly ducking = new DuckingController();
  private settings: AudioSettings;
  private readonly persist: boolean;
  private readonly forceSynth: boolean;
  private lastDuckMul = 1;
  private lastLieVoDuck = 1;
  /** last play time (ms) per clip for fatigue cooldowns */
  private lastClipPlayMs = new Map<ClipId, number>();
  private disposed = false;
  /** Product wants music beds when assets exist (enabled after preload). */
  private readonly musicWanted: boolean;

  constructor(
    settings: AudioSettings,
    persist: boolean,
    search: string,
    musicContent: boolean,
  ) {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) {
      throw new Error("AudioContext not available");
    }
    this.context = new Ctx();
    this.graph = createBusGraph(this.context);
    this.loader = new AudioLoader(this.context);
    this.sfx = new SfxPlayer(this.context, this.graph);
    this.state = new StateSonifier(this.context, this.graph, (id) =>
      this.peekBuffer(id),
    );
    this.music = new MusicDirector(this.context, this.graph, (id) =>
      this.peekBuffer(id),
    );
    this.voice = new VoicePlayer(this.context, this.graph, this.loader);
    this.lieVoice = new LieVoicePlayer(this.context, this.graph, this.loader);
    // Do not enable music until preload — avoids "foundation not loaded" on boot
    this.musicWanted = musicContent;
    this.music.setContentEnabled(false);
    this.settings = settings;
    this.persist = persist;
    this.forceSynth = forceAudioSynth(search);
    this.reapplyBusGains(0);

    try {
      (globalThis as { __SOTS_AUDIO_CTX_CREATED__?: boolean }).__SOTS_AUDIO_CTX_CREATED__ =
        true;
    } catch {
      /* ignore */
    }
  }

  async resume(): Promise<void> {
    if (this.disposed) return;
    if (this.context.state === "suspended") {
      await this.context.resume();
    }
    // Restart music loops after resume — suspended starts can stay silent
    if (this.context.state === "running") {
      this.music.kick();
    }
  }

  async suspend(): Promise<void> {
    if (this.disposed) return;
    if (this.context.state === "running") {
      await this.context.suspend();
    }
  }

  async preload(ids: string[]): Promise<void> {
    if (this.disposed) return;
    const all = [
      ...new Set([...ids, ...PRELOAD_STATE_IDS, ...PRELOAD_MUSIC_IDS]),
    ];
    // Load music first so foundation is ready before tryStart
    await Promise.all(
      PRELOAD_MUSIC_IDS.map((id) =>
        this.ensureBuffer(id).catch((err) => {
          // eslint-disable-next-line no-console
          console.warn(`[audio] music preload failed for ${id}`, err);
          return null;
        }),
      ),
    );
    await Promise.all(
      all.map((id) =>
        this.ensureBuffer(id).catch(() => null),
      ),
    );
    // Spoken Word pack (Proclaim)
    const voIds = ids
      .filter((id) => id.startsWith("vo:"))
      .map((id) => id.slice(3));
    const verseIds =
      voIds.length > 0
        ? voIds
        : ids.some((id) => id === "preload:proclaim-vo")
          ? [...PROCLAIM_VO_IDS]
          : [];
    if (verseIds.length > 0) {
      await this.voice.preload(verseIds).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[audio] VO preload failed", err);
      });
    }
    if (ids.includes("preload:proclaim-lie-vo") || ids.includes("preload:proclaim-vo")) {
      await this.lieVoice.preload().catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[audio] lie VO preload failed", err);
      });
    }
    this.state.tryStart();

    // Ensure context is running before starting loop sources
    if (this.context.state === "suspended") {
      try {
        await this.context.resume();
      } catch {
        /* autoplay may still block until gesture — caller already gestured */
      }
    }

    this.music.markBuffersExpected();
    const foundation = this.peekBuffer("music.foundation");
    if (foundation && this.musicWanted) {
      this.music.setContentEnabled(true);
      // Start only if context is already running (post-gesture unlock)
      this.music.kick();
    } else if (this.musicWanted) {
      // eslint-disable-next-line no-console
      console.warn(
        "[audio] music.foundation missing after preload — check /audio/music/foundation.mp3",
      );
    }
  }

  emit(e: AudioEvent): void {
    if (this.disposed) return;

    // Ducking tracks reveal/proclaim even when muted (correct restore on unmute)
    const prevDuck = this.lastDuckMul;
    this.ducking.handleEvent(e);

    // Spoken Word — before SFX so duck can include voicePlaying this frame
    if (e.type === "proclaim.start") {
      // Immediately duck lie claims under the coming Word
      this.ducking.setVoicePlaying(true);
      void this.voice
        .start(e.verseId, this.settings)
        .then((started) => {
          if (this.disposed) return;
          this.ducking.setVoicePlaying(started);
          this.reapplyBusGains(DUCK_ATTACK_S);
        });
    } else if (e.type === "proclaim.end") {
      this.voice.stop();
      this.ducking.setVoicePlaying(false);
    } else if (e.type === "lie.spawn" || e.type === "lie.degrade") {
      // One claim VO at a time; play() is a no-op if already speaking
      if (!this.settings.minimal) {
        const voKey = e.voId ?? e.lieId;
        const fallback = e.voId && e.voId !== e.lieId ? e.lieId : undefined;
        void this.lieVoice.play(voKey, e.level, this.settings, fallback);
      }
    } else if (e.type === "lie.silence") {
      // Cut claim mid-whisper when the lie is fully silenced
      this.lieVoice.stop();
    } else if (
      e.type === "mission.start" ||
      e.type === "mission.won" ||
      e.type === "mission.lost"
    ) {
      this.voice.stop();
      this.lieVoice.stop();
      this.ducking.setVoicePlaying(false);
    } else if (e.type === "pause") {
      this.voice.onPause(e.paused, this.settings);
      this.ducking.setVoicePlaying(this.voice.isPlaying());
    }

    const nextDuck = this.ducking.multiplier(this.settings);
    const nextLieDuck = this.ducking.lieVoMultiplier(this.settings);
    if (
      Math.abs(nextDuck - prevDuck) > 0.001 ||
      Math.abs(nextLieDuck - this.lastLieVoDuck) > 0.001
    ) {
      const ramp = this.ducking.rampSeconds(
        Math.min(prevDuck, this.lastLieVoDuck),
        Math.min(nextDuck, nextLieDuck),
      );
      this.lastDuckMul = nextDuck;
      this.lastLieVoDuck = nextLieDuck;
      this.reapplyBusGains(ramp);
    }

    if (this.settings.muted) return;

    let clipIds = resolveEventClips(e);
    if (this.settings.minimal) {
      clipIds = clipIds.filter((id) => clipAllowedInMinimal(id));
    }
    for (const id of clipIds) {
      if (!this.passesCooldown(id)) continue;
      void this.playClip(id);
    }
  }

  tick(frame: AudioFrameInput, dt: number): void {
    if (this.disposed) return;
    this.state.tick(frame, dt, this.settings);
    this.music.tick(frame, this.settings);
    this.voice.tick(frame);
    this.lieVoice.tick(frame);
  }

  setBusVolume(bus: AudioBusId, v: number): void {
    if (this.disposed) return;
    if (bus === "master") {
      this.applySettings({ master: v });
    } else {
      this.applySettings({ [bus]: v });
    }
  }

  getSettings(): AudioSettings {
    return { ...this.settings };
  }

  applySettings(partial: Partial<AudioSettings>): void {
    if (this.disposed) return;
    const prevVoice = this.settings.voiceEnabled;
    this.settings = mergeAudioSettings(this.settings, partial);
    if (prevVoice && !this.settings.voiceEnabled) {
      this.voice.stop();
      this.ducking.setVoicePlaying(false);
    }
    if (!this.settings.lieVoEnabled) {
      this.lieVoice.stop();
    }
    this.reapplyBusGains(0.05);
    if (this.persist) saveAudioSettings(this.settings);
  }

  createKaraokeDriver(): ProclaimKaraokeDriver {
    return this.voice.createKaraokeDriver();
  }

  /**
   * Study preview of spoken Word. Does not fire proclaim SFX / seals.
   * Ignores combat `voiceEnabled` so you can still hear verses while studying
   * (still respects master mute).
   */
  async playVerseVo(verseId: string): Promise<boolean> {
    if (this.disposed || !verseId) return false;
    if (this.context.state === "suspended") {
      try {
        await this.context.resume();
      } catch {
        return false;
      }
    }
    if (this.settings.muted) return false;

    this.ducking.setVoicePlaying(true);
    this.reapplyBusGains(DUCK_ATTACK_S);

    const started = await this.voice.start(verseId, {
      voiceEnabled: true,
      muted: false,
      voice: this.settings.voice,
    });
    this.ducking.setVoicePlaying(started);
    this.reapplyBusGains(started ? DUCK_ATTACK_S : 0.05);
    return started;
  }

  stopVerseVo(): void {
    if (this.disposed) return;
    this.voice.stop();
    this.ducking.setVoicePlaying(false);
    this.reapplyBusGains(0.08);
  }

  isVerseVoPlaying(verseId?: string): boolean {
    if (this.disposed || !this.voice.isPlaying()) return false;
    if (!verseId) return true;
    return this.voice.getActiveVerseId() === verseId;
  }

  resetMission(): void {
    this.sfx.stopAll();
    this.state.resetMission();
    this.music.resetMission();
    this.voice.resetMission();
    this.lieVoice.resetMission();
    this.ducking.reset();
    this.lastDuckMul = 1;
    this.lastLieVoDuck = 1;
    this.lastClipPlayMs.clear();
    this.reapplyBusGains(0.1);
  }

  /** Rate-limit spammy board SFX (spawn/press/overflow). */
  private passesCooldown(id: ClipId): boolean {
    const cd = CLIP_COOLDOWN_S[id];
    if (cd === undefined || cd <= 0) return true;
    const now = performance.now();
    const last = this.lastClipPlayMs.get(id) ?? 0;
    if (now - last < cd * 1000) return false;
    this.lastClipPlayMs.set(id, now);
    return true;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.voice.dispose();
    this.lieVoice.dispose();
    this.music.dispose();
    this.state.dispose();
    this.sfx.dispose();
    this.loader.clear();
    this.graph.dispose();
    void this.context.close();
  }

  /** Debug / future UI: current adaptive threat estimate. */
  getMusicThreat(): number {
    return this.music.getThreat();
  }

  private reapplyBusGains(rampSec: number): void {
    const duck = this.ducking.multiplier(this.settings);
    const lieDuck = this.ducking.lieVoMultiplier(this.settings);
    this.lastDuckMul = duck;
    this.lastLieVoDuck = lieDuck;
    this.graph.applySettings(this.settings, {
      musicDuck: duck,
      ambientDuck: duck,
      lieVoDuck: lieDuck,
      rampSec,
    });
  }

  private peekBuffer(id: ClipId): AudioBuffer | null {
    const cached = this.loader.peek(id);
    if (cached) return cached;
    if (allowDevSynthFallback() || this.forceSynth) {
      const entry = getCatalogEntry(id);
      if (!entry) return null;
      return this.synthAndCache(id, entry.loop === true);
    }
    return null;
  }

  private async ensureBuffer(id: ClipId): Promise<AudioBuffer | null> {
    if (this.disposed) return null;
    const entry = getCatalogEntry(id);
    if (!entry) return null;

    if (this.forceSynth && allowDevSynthFallback()) {
      return this.synthAndCache(id, entry.loop === true);
    }

    const cached = this.loader.peek(id);
    if (cached !== undefined) {
      if (cached) return cached;
      if (allowDevSynthFallback()) {
        return this.synthAndCache(id, entry.loop === true);
      }
      return null;
    }

    const loaded = await this.loader.load(id, entry.path);
    if (loaded) return loaded;

    if (allowDevSynthFallback()) {
      return this.synthAndCache(id, entry.loop === true);
    }
    return null;
  }

  private synthAndCache(id: ClipId, loop: boolean): AudioBuffer {
    const existing = this.loader.peek(id);
    if (existing) return existing;

    const buffer = loop
      ? renderLoopBedBuffer(this.context, id)
      : renderSynthBuffer(this.context, id);
    this.loader.put(id, buffer);
    return buffer;
  }

  private async playClip(id: ClipId): Promise<void> {
    if (this.disposed) return;
    const entry = getCatalogEntry(id);
    if (!entry) return;

    if (entry.bus === "voice" && !this.settings.voiceEnabled) return;

    const buffer = await this.ensureBuffer(id);
    if (!buffer || this.disposed) return;

    if (entry.loop) return;

    this.sfx.play(buffer, {
      bus: entry.bus,
      gain: entry.gain ?? 1,
    });
  }

  getAudioContext(): AudioContext {
    return this.context;
  }
}

/**
 * Deferred engine: no AudioContext until first resume/preload/emit after a
 * user gesture. Avoids autoplay-policy console spam on page load.
 */
function createDeferredWebEngine(
  initial: AudioSettings,
  persist: boolean,
  search: string,
  musicContent: boolean,
): AudioEngine {
  let inner: WebAudioEngine | null = null;
  let settings = { ...initial };
  let disposed = false;

  const ensure = (): WebAudioEngine | null => {
    if (disposed) return null;
    if (inner) return inner;
    try {
      inner = new WebAudioEngine(settings, persist, search, musicContent);
      // Re-apply any settings the panel changed before unlock
      inner.applySettings(settings);
      return inner;
    } catch {
      return null;
    }
  };

  return {
    kind: "web",
    async resume() {
      const eng = ensure();
      if (eng) await eng.resume();
    },
    async suspend() {
      if (inner) await inner.suspend();
    },
    async preload(ids: string[]) {
      const eng = ensure();
      if (eng) await eng.preload(ids);
    },
    emit(e: AudioEvent) {
      // Need context for one-shots — only after unlock path has called resume first
      const eng = ensure();
      eng?.emit(e);
    },
    tick(frame: AudioFrameInput, dt: number) {
      // No context yet → no beds (pre-gesture)
      inner?.tick(frame, dt);
    },
    setBusVolume(bus: AudioBusId, v: number) {
      if (bus === "master") {
        settings = mergeAudioSettings(settings, { master: v });
      } else {
        settings = mergeAudioSettings(settings, { [bus]: v });
      }
      inner?.setBusVolume(bus, v);
    },
    getSettings() {
      return inner ? inner.getSettings() : { ...settings };
    },
    applySettings(partial: Partial<AudioSettings>) {
      settings = mergeAudioSettings(settings, partial);
      if (persist) saveAudioSettings(settings);
      inner?.applySettings(partial);
    },
    createKaraokeDriver(): ProclaimKaraokeDriver {
      return inner?.createKaraokeDriver() ?? linearKaraokeDriver();
    },
    async playVerseVo(verseId: string) {
      const eng = ensure();
      if (!eng) return false;
      await eng.resume();
      return eng.playVerseVo(verseId);
    },
    stopVerseVo() {
      inner?.stopVerseVo();
    },
    isVerseVoPlaying(verseId?: string) {
      return inner?.isVerseVoPlaying(verseId) ?? false;
    },
    resetMission() {
      inner?.resetMission();
    },
    dispose() {
      disposed = true;
      inner?.dispose();
      inner = null;
    },
  };
}

/**
 * Create the shared audio engine.
 *
 * **Import hygiene:** call only from prototype `main.ts` / menu — never from Game.ts.
 * AudioContext is created on first resume/preload (after user gesture), not at import.
 */
export function createAudioEngine(
  opts: CreateAudioEngineOptions = {},
): AudioEngine {
  const search =
    opts.search ??
    (typeof location !== "undefined" ? location.search : "");
  const queryOff = audioDisabledByQuery(search);
  const enabled = opts.enabled ?? true;

  if (!enabled || queryOff) {
    const base = opts.settings ?? loadAudioSettings();
    return createNullEngine(
      queryOff ? mergeAudioSettings(base, { muted: true }) : base,
    );
  }

  if (typeof window === "undefined") {
    return createNullEngine(opts.settings ?? DEFAULT_AUDIO_SETTINGS);
  }

  const Ctx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctx) {
    return createNullEngine(opts.settings ?? loadAudioSettings());
  }

  const settings = opts.settings ?? loadAudioSettings();
  const persist = opts.persist ?? true;
  return createDeferredWebEngine(
    settings,
    persist,
    search,
    opts.musicContent ?? false,
  );
}

export type { AudioEngine };
